import { defineStore } from 'pinia'

import type { EtgAlertType } from '@/components/common/etg-alert.vue'
import type { IconName } from '@/types/icon'

const RESET_TIME = 5000
const ANIMATION_TIME = 300

type AlertArgs = { isClosable?: boolean; type?: EtgAlertType; iconName?: IconName }

export const useAlertStore = defineStore('alert', () => {
  const isAlertShown = ref(false)
  const isAlertClosable = ref(false)
  const alertMessage = ref('')
  const alertType = ref<EtgAlertType>('success')
  const icon = ref<IconName | undefined>()
  const { start: startHideAlertTimer } = useTimeoutFn(hide, RESET_TIME, { immediate: false })
  const { start: startClearAlertTimer } = useTimeoutFn(_clear, ANIMATION_TIME, { immediate: false })

  watch(isAlertShown, newIsAlertShown => {
    if (!newIsAlertShown) {
      startClearAlertTimer()
    }
  })

  function show({ message, isClosable, type, iconName }: { message: string } & AlertArgs) {
    alertType.value = type
    alertMessage.value = message
    isAlertClosable.value = isClosable || false
    isAlertShown.value = true
    icon.value = iconName
    startHideAlertTimer()
  }

  function success(message: string, args: AlertArgs = {}) {
    show({ message, type: 'success', ...args })
  }

  function error(message: string, args: AlertArgs = {}) {
    show({ message, type: 'error', ...args })
  }

  function hide() {
    isAlertShown.value = false
  }

  function _clear() {
    alertType.value = 'success'
    alertMessage.value = ''
    isAlertShown.value = false
    icon.value = undefined
  }

  return {
    isAlertShown,
    isAlertClosable,
    alertMessage,
    alertType,
    icon,
    success,
    error,
    show,
    hide,
  }
})
